import React, { useEffect } from 'react';
import { Form } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { useSelector, } from 'react-redux';
import moment from 'moment';

import { FormStep } from 'types/forms/formTypes';
import { DroneAccreditationSelectorFormData } from 'types/forms/droneAccreditationTypes';
import { Button, Container } from 'components/common';
import {
  FormAlert,
  GenericStep,
  ApplicationName,
  ControlInputGroup
} from 'components/CASAForm';
import {
  AuBreadcrumbs,
  IAuBreadcrumbItem,
  AuCallout
} from 'components/DTA';

import { featureSelector, urlSelector } from 'PortalStore/Selectors/EnvDataSelectors';

import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { AccreditationConstants } from 'contexts/LmsContext';

import { FORM_ERROR_MESSAGES } from 'internalConstants';
import { getPortalUrl } from 'components/Portal/Utilities';

const DroneAccreditationSelector: React.FC = () => {
  const history = useHistory();
  const query = queryString.parse(location.search);

  const featureAccredRec = useSelector(state => featureSelector(state, 'Portal - Feature Availability - Recreational Drone'));
  const recAccredDate = moment(featureAccredRec, 'DD/MM/YYYY');
  const recAvailable = recAccredDate.isBefore();

  const initialValues: DroneAccreditationSelectorFormData = {
    assessmentType: ''
  };

  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const urlHome = useSelector((state: any) => urlSelector(state, 'Portal - Home URL'));
  const getDashboardUrl = () => {
    return getPortalUrl('/dashind', B2CSettings.items.standalone, urlHome);
  };
  const dashboardUrl = getDashboardUrl();

  // Steps and step validation associated with the form
  const FORM_STEPS: FormStep[] = [
    {
      id: 'AccreditationSelector',
      render: () => (
        <GenericStep
          hidePrevious
          showSaveAndExit={false}
          nextButtonText='Next'
          triggerFormValidation={true}
          customHandleBack={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            window.location.href = dashboardUrl;
          }}>
          <AuCallout>
            You can apply for either recreational or commercial accreditation below. You can learn more about what accreditation is and what both mean on the <Button link className='no-padding' onClick={() => window.open('https://www.casa.gov.au/drones/accreditation', '_blank')}>CASA website</Button>.
          </AuCallout>

          <ControlInputGroup
            name='assessmentType'
            label='Which accreditation would you like to apply for?'
            type={'radio'}
            options={[
              {
                label: 'Recreational drone operator accreditation',
                value: AccreditationConstants.RECREATIONAL_ASSESSMENT_TYPE
              },
              {
                label: <>Commercial <abbr title="Remotely piloted aircraft">RPA </abbr> operator accreditation</>,
                value: AccreditationConstants.COMMERCIAL_ASSESSMENT_TYPE
              }
            ]}
          />
        </GenericStep>
      ),
      validationSchema: yup.object().shape({
        assessmentType: yup.string().trim()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      })
    }
  ];

  const handleSubmit = async (values: DroneAccreditationSelectorFormData): Promise<void> => {
    // Open the appropriate form based on selected assessment type
    switch (values.assessmentType) {
      case AccreditationConstants.RECREATIONAL_ASSESSMENT_TYPE:
        history.push('/drone-accreditation/recreational');
        break;
      case AccreditationConstants.COMMERCIAL_ASSESSMENT_TYPE:
        history.push('/drone-accreditation/commercial');
        break;
    }
  };

  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: dashboardUrl, text: 'myCASA' },
    { text: 'Drone accreditation' }
  ];

  // runs on the first render
  useEffect(() => {
    // If rec drones is not available go immediately to the commercial step
    if (!recAvailable) {
      history.push('/drone-accreditation/commercial');
    }
  }, []);

  return (
    <Container>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={FORM_STEPS}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps, formAlert, setFormAlert }: IFormContextType) => {
            // TODO: confirm error message text
            if (query?.error && !formAlert) {
              setFormAlert({
                title: 'Failed to retrieve assessment please refresh your browser.',
                as: 'error'
              });
            }

            return (
              <Form name='Drone accreditation'>
                <FormAlert htmlFor='Drone accreditation' />
                <ApplicationName name='Drone accreditation' />
                {steps[currentStepIndex].render(currentStepProps)}
              </Form>
            );
          }}
        </FormContext.Consumer>
      </FormContextProvider>
    </Container>
  );
};

export default DroneAccreditationSelector;